// Las versiones de una edición en el visor — ADR-2026-10-06-versiones-de-la-edicion-en-el-visor.
//
// Una columna de miniaturas a la izquierda del visor mientras se edita: "Versión original"
// (la imagen con la que se entró) y "Versión 2", "Versión 3"… en el orden en que salieron.
// Tocar una la muestra en el visor, y la próxima edición parte de ella. Mientras se genera
// hay una miniatura en espera con el brillo de la edición; cada versión nueva entra con
// `version-entra` (index.css): sube un poco y se agranda desde 0,9 mientras aparece.

import CachedImg from './CachedImg';

export interface VersionDelVisor {
  id: string;
  /** La miniatura (opt/ o, si todavía no está, full/). */
  src: string;
  /** Una marca chica sobre la miniatura (la versión mejorada). */
  etiqueta?: string;
}

export const rotuloDeVersion = (i: number): string => (i === 0 ? 'Versión original' : `Versión ${i + 1}`);

export function VersionesDeEdicion({
  versiones,
  actualId,
  pendiente,
  visible,
  onElegir,
  enFila = false,
}: {
  versiones: readonly VersionDelVisor[];
  actualId: string;
  /** Hay una versión generándose: se muestra su lugar en espera. */
  pendiente: boolean;
  visible: boolean;
  onElegir: (id: string) => void;
  /** Una tira horizontal en el lugar donde se la ponga (el visor del teléfono, entre la
   *  imagen y el prompt) en vez de la columna flotante de la izquierda
   *  (ADR-2026-10-28-editar-en-el-visor-del-celular). */
  enFila?: boolean;
}) {
  if (enFila)
    return (
      <div
        role="listbox"
        aria-label="Versiones de la imagen"
        data-sin-deslizar
        className="flex items-start gap-1 overflow-x-auto px-3 scrollbar-hide"
        style={{ touchAction: 'pan-x' }}
      >
        {versiones.map((v, i) => {
          const activa = v.id === actualId;
          return (
            <button
              key={v.id}
              type="button"
              role="option"
              aria-selected={activa}
              aria-label={rotuloDeVersion(i)}
              onClick={() => onElegir(v.id)}
              className="version-entra flex w-[60px] shrink-0 flex-col items-center gap-1 p-1 text-center"
            >
              <span
                className={`relative block h-12 w-12 overflow-hidden rounded-sm ring-2 transition-[box-shadow,opacity] duration-150 ${
                  activa ? 'ring-white' : 'ring-transparent opacity-60'
                }`}
              >
                <CachedImg src={v.src} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
                {v.etiqueta && (
                  <span className="absolute inset-x-0 bottom-0 bg-black/60 py-px text-[8.5px] font-semibold uppercase tracking-wide text-white">
                    {v.etiqueta}
                  </span>
                )}
              </span>
              <span className={`text-[10.5px] font-medium leading-tight ${activa ? 'text-white' : 'text-white/50'}`}>
                {i === 0 ? 'Original' : `V${i + 1}`}
              </span>
            </button>
          );
        })}
        {pendiente && (
          <div className="version-entra flex w-[60px] shrink-0 flex-col items-center gap-1 p-1" aria-label="Generando una versión nueva">
            <span className="relative block h-12 w-12 overflow-hidden rounded-sm bg-white/[0.08]">
              <span className="brillo-de-edicion" />
            </span>
            <span className="text-[10.5px] font-medium leading-tight text-white/40">V{versiones.length + 1}</span>
          </div>
        )}
      </div>
    );
  return (
    <div
      aria-hidden={!visible}
      {...((!visible ? { inert: '' } : {}) as Record<string, unknown>)}
      className="absolute left-6 top-1/2 z-30 -translate-y-1/2"
    >
      <div
        role="listbox"
        aria-label="Versiones de la imagen"
        className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto rounded-xl border border-white/[0.08] bg-white/[0.06] p-2 backdrop-blur-xl scrollbar-hide"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'none' : 'translateX(-12px)',
          transition: 'opacity 240ms ease-out, transform 340ms cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {versiones.map((v, i) => {
          const activa = v.id === actualId;
          return (
            <button
              key={v.id}
              type="button"
              role="option"
              aria-selected={activa}
              onClick={() => onElegir(v.id)}
              className="version-entra group flex w-[92px] flex-col items-center gap-1.5 rounded-lg p-1.5 text-center transition-colors hover:bg-white/[0.06]"
            >
              <span
                className={`relative block h-[72px] w-[72px] overflow-hidden rounded-md ring-2 transition-[box-shadow,opacity] duration-200 ${
                  activa ? 'ring-white' : 'ring-transparent opacity-70 group-hover:opacity-100'
                }`}
              >
                <CachedImg src={v.src} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
                {v.etiqueta && (
                  <span className="absolute bottom-1 left-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
                    {v.etiqueta}
                  </span>
                )}
              </span>
              <span className={`text-[11.5px] font-medium leading-tight ${activa ? 'text-white' : 'text-white/55'}`}>
                {rotuloDeVersion(i)}
              </span>
            </button>
          );
        })}
        {pendiente && (
          <div className="version-entra flex w-[92px] flex-col items-center gap-1.5 p-1.5" aria-label="Generando una versión nueva">
            <span className="relative block h-[72px] w-[72px] overflow-hidden rounded-md bg-white/[0.08]">
              <span className="brillo-de-edicion" />
            </span>
            <span className="text-[11.5px] font-medium leading-tight text-white/40">{rotuloDeVersion(versiones.length)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default VersionesDeEdicion;
